import * as React from 'react';
import type { GuestVoucherDto } from './GuestVoucherCard';

interface RevertModalProps {
    voucher: GuestVoucherDto | null;
    onClose: () => void;
    onReverted: () => void;
}

type StepStatus = 'pending' | 'running' | 'completed' | 'failed';

interface StepState {
    key: string;
    label: string;
    status: StepStatus;
    error?: string;
}

const ON_SITE_STEPS: { key: string; label: string }[] = [
    { key: 'validate', label: 'Validating voucher' },
    { key: 'deallocate', label: 'Removing allocation' },
    { key: 'sync', label: 'Syncing changes' },
    { key: 'cancel_booking', label: 'Cancelling booking' },
    { key: 'void_order', label: 'Voiding order' },
    { key: 'restore', label: 'Restoring voucher' },
];

function StepIcon({ status }: { status: StepStatus }) {
    switch (status) {
        case 'pending':
            return <span className="text-muted" style={{ width: '20px', display: 'inline-block', textAlign: 'center' }}>○</span>;
        case 'running':
            return (
                <span style={{ width: '20px', display: 'inline-block', textAlign: 'center' }}>
                    <span className="spinner-border spinner-border-sm text-primary" role="status" style={{ width: '14px', height: '14px' }} />
                </span>
            );
        case 'completed':
            return <span className="text-success" style={{ width: '20px', display: 'inline-block', textAlign: 'center' }}>✓</span>;
        case 'failed':
            return <span className="text-danger" style={{ width: '20px', display: 'inline-block', textAlign: 'center' }}>✗</span>;
    }
}

export const RevertModal: React.FC<RevertModalProps> = ({ voucher, onClose, onReverted }) => {
    const [phase, setPhase] = React.useState<'confirm' | 'processing' | 'done' | 'failed'>('confirm');
    const [steps, setSteps] = React.useState<StepState[]>([]);
    const [globalError, setGlobalError] = React.useState<string | null>(null);
    const [externalSubmitting, setExternalSubmitting] = React.useState(false);

    // Track active EventSource for cleanup
    const eventSourceRef = React.useRef<EventSource | null>(null);

    // Track the voucher ID so we only reset when a genuinely different voucher is opened
    const voucherId = voucher?.guestVoucherId;

    // Attach EventSource to a flow and wire up step/flow events
    const attachToFlow = React.useCallback((flowId: string, initialSteps: StepState[]) => {
        const es = new EventSource(`/api/flows/${flowId}/progress`);
        eventSourceRef.current = es;

        let currentSteps = [...initialSteps];

        es.addEventListener('init', (e: MessageEvent) => {
            try {
                const data = JSON.parse(e.data) as {
                    steps: { step: string; status: string; label?: string; error?: string }[];
                };
                currentSteps = currentSteps.map(s => {
                    const serverStep = data.steps.find(ss => ss.step === s.key);
                    if (serverStep) {
                        return {
                            ...s,
                            status: serverStep.status as StepStatus,
                            label: serverStep.label || s.label,
                            error: serverStep.error,
                        };
                    }
                    return s;
                });
                setSteps([...currentSteps]);
            } catch { /* skip */ }
        });

        es.addEventListener('step', (e: MessageEvent) => {
            try {
                const data = JSON.parse(e.data) as {
                    step: string;
                    status: string;
                    label?: string;
                    error?: string;
                };
                currentSteps = currentSteps.map(s => {
                    if (s.key === data.step) {
                        return {
                            ...s,
                            status: data.status as StepStatus,
                            label: data.label || s.label,
                            error: data.error,
                        };
                    }
                    return s;
                });
                setSteps([...currentSteps]);
            } catch { /* skip */ }
        });

        es.addEventListener('flow', (e: MessageEvent) => {
            try {
                const data = JSON.parse(e.data) as { status: string; error?: string };
                if (data.status === 'completed') {
                    setPhase('done');
                } else {
                    if (data.error) setGlobalError(data.error);
                    setPhase('failed');
                }
            } catch {
                setPhase('failed');
            }
            es.close();
            eventSourceRef.current = null;
        });

        es.onerror = () => {
            if (es.readyState === EventSource.CLOSED) {
                setGlobalError('Lost connection to server');
                setPhase('failed');
                eventSourceRef.current = null;
            }
        };
    }, []);

    // Reset state when voucher changes — re-attach to active flow if one exists
    React.useEffect(() => {
        if (!voucher) return;
        setSteps([]);
        setGlobalError(null);
        setExternalSubmitting(false);

        // If a revert flow is already running, skip confirm and re-attach to SSE
        if (voucher.activeFlowId && !voucher.externalRedemption) {
            const initialSteps: StepState[] = ON_SITE_STEPS.map(s => ({
                key: s.key, label: s.label, status: 'pending' as StepStatus,
            }));
            setSteps(initialSteps);
            setPhase('processing');
            attachToFlow(voucher.activeFlowId, initialSteps);
            return;
        }

        setPhase('confirm');
    }, [voucherId, attachToFlow]);

    // Cleanup EventSource on unmount
    React.useEffect(() => {
        return () => {
            eventSourceRef.current?.close();
        };
    }, []);

    const isExternal = voucher?.externalRedemption ?? false;

    const handleConfirmExternal = async () => {
        if (!voucher) return;
        setExternalSubmitting(true);
        setGlobalError(null);
        try {
            const response = await fetch(`/api/sales/guest-vouchers/${voucher.guestVoucherId}/revert-reconciliation`, {
                method: 'POST',
            });
            if (!response.ok) {
                const result = await response.json();
                setGlobalError(result.error || 'Revert failed');
                setPhase('failed');
            } else {
                setPhase('done');
            }
        } catch (e: any) {
            setGlobalError(e.message || 'Network error');
            setPhase('failed');
        } finally {
            setExternalSubmitting(false);
        }
    };

    const handleConfirmOnSite = async () => {
        if (!voucher) return;

        const initialSteps: StepState[] = ON_SITE_STEPS.map(s => ({
            key: s.key,
            label: s.label,
            status: 'pending' as StepStatus,
        }));
        setSteps(initialSteps);
        setPhase('processing');
        setGlobalError(null);

        try {
            const response = await fetch(`/api/sales/guest-vouchers/${voucher.guestVoucherId}/revert`, {
                method: 'POST',
            });

            if (!response.ok) {
                try {
                    const err = await response.json();
                    setGlobalError(err.error || `HTTP ${response.status}`);
                } catch {
                    setGlobalError(`HTTP ${response.status}`);
                }
                setPhase('failed');
                return;
            }

            const result = await response.json();

            if (result.alreadyReverted) {
                setSteps(initialSteps.map(s => ({ ...s, status: 'completed' as StepStatus })));
                setPhase('done');
                return;
            }

            // Server returns { flowId } for new flows, { activeFlowId } if another user already started one
            const targetFlowId = result.flowId || result.activeFlowId;
            attachToFlow(targetFlowId, initialSteps);
        } catch (e: any) {
            setGlobalError(e.message || 'Network error');
            setPhase('failed');
        }
    };

    const handleClose = () => {
        eventSourceRef.current?.close();
        eventSourceRef.current = null;
        if (phase === 'done') {
            onReverted();
        }
        onClose();
    };

    if (!voucher) return null;

    return (
        <>
            {/* Backdrop */}
            <div
                className="position-fixed top-0 start-0 w-100 h-100"
                style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}
                onClick={phase === 'processing' ? undefined : handleClose}
            />
            {/* Modal dialog */}
            <div
                className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                style={{ zIndex: 1061, pointerEvents: 'none' }}
            >
                <div
                    className="card shadow-lg"
                    style={{ width: '100%', maxWidth: '460px', pointerEvents: 'auto' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Revert V-{voucher.voucherNumber}</h5>
                        {phase !== 'processing' && (
                            <button type="button" className="btn-close" onClick={handleClose} aria-label="Close" />
                        )}
                    </div>
                    <div className="card-body">
                        {globalError && (
                            <div className="alert alert-danger py-2 small" role="alert">
                                {globalError}
                            </div>
                        )}

                        {/* Confirmation phase */}
                        {phase === 'confirm' && (
                            <>
                                <p className="text-muted small mb-2">
                                    {isExternal ? (
                                        <>
                                            This will clear the reconciliation data for voucher <strong>V-{voucher.voucherNumber}</strong> ({voucher.voucherType}).
                                            The voucher will be restored to <strong>Active</strong>.
                                        </>
                                    ) : (
                                        <>
                                            Reverting voucher <strong>V-{voucher.voucherNumber}</strong> ({voucher.voucherType}) will:
                                        </>
                                    )}
                                </p>
                                {!isExternal && (
                                    <ul className="small text-muted mb-3">
                                        <li>Remove the allocation from the booking</li>
                                        <li>Cancel the booking (No Charge)</li>
                                        <li>Void the order</li>
                                        <li>Restore the voucher to <strong>Active</strong></li>
                                    </ul>
                                )}
                                {voucher.redeemedServiceType && (
                                    <div className="small text-muted mb-2">
                                        Currently redeemed as: <strong>{voucher.redeemedServiceType}</strong>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Processing phase — step list */}
                        {(phase === 'processing' || phase === 'done' || (phase === 'failed' && steps.length > 0)) && !isExternal && (
                            <div className="mb-2">
                                {steps.map((step) => (
                                    <div
                                        key={step.key}
                                        className={`d-flex align-items-start gap-2 py-1 ${step.status === 'pending' ? 'text-muted' : ''}`}
                                    >
                                        <StepIcon status={step.status} />
                                        <div>
                                            <span className={step.status === 'pending' ? 'text-muted' : step.status === 'failed' ? 'text-danger' : ''}>
                                                {step.label}
                                            </span>
                                            {step.error && (
                                                <div className="text-danger small mt-1">{step.error}</div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Done state */}
                        {phase === 'done' && (
                            <div className="alert alert-success py-2 mb-0 small">
                                <strong>Voucher reverted successfully.</strong> The voucher is now Active.
                            </div>
                        )}
                    </div>
                    <div className="card-footer d-flex justify-content-end gap-2">
                        {phase === 'confirm' && (
                            <>
                                <button className="btn btn-secondary btn-sm" onClick={handleClose}>
                                    Cancel
                                </button>
                                <button
                                    className="btn btn-warning btn-sm"
                                    onClick={isExternal ? handleConfirmExternal : handleConfirmOnSite}
                                    disabled={externalSubmitting}
                                >
                                    {externalSubmitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-1" role="status" />
                                            Reverting...
                                        </>
                                    ) : (
                                        'Confirm Revert'
                                    )}
                                </button>
                            </>
                        )}
                        {phase === 'processing' && (
                            <button className="btn btn-secondary btn-sm" disabled>
                                <span className="spinner-border spinner-border-sm me-1" role="status" />
                                Processing...
                            </button>
                        )}
                        {(phase === 'done' || phase === 'failed') && (
                            <button className="btn btn-secondary btn-sm" onClick={handleClose}>
                                Close
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};
